// shared/base-rate-history.js — the Bank of England's Bank Rate, as it has
// changed, and the rate statutory interest under the Late Payment of
// Commercial Debts (Interest) Act 1998 is charged at for a debt.
//
// Pure: no imports, no DOM, no node builtins, so the server, the browser and
// the iOS app's bundle all read the same table.
//
// THE LAW. The Late Payment of Commercial Debts (Rate of Interest) (No. 3)
// Order 2002, article 4 (https://www.legislation.gov.uk/uksi/2002/1675/article/4/made):
// the rate is 8% a year over "the official dealing rate in force on the 30th
// June (in respect of interest which starts to run between 1st July and 31st
// December) or the 31st December (in respect of interest which starts to run
// between 1st January and 30th June) immediately before the day on which
// statutory interest starts to run". The official dealing rate is Bank Rate.
// Interest starts to run the day after the debt's due date (s.4(2) of the
// Act). So each debt has ONE rate, fixed by its reference date, for as long as
// it runs, and two debts that went overdue in different half-years can carry
// different rates. One company-wide figure, which this replaced, was right
// for neither.
//
// SOURCE. Every change below is from the Bank of England's own database,
// "Bank Rate history and data":
// https://www.bankofengland.co.uk/boeapps/database/Bank-Rate.asp
// Checked against it on BANK_RATE_CHECKED_ON, when Bank Rate stood at 3.75%
// (held since 18 December 2025; the next decision was due 5 November 2026).
// The table starts at the rate in force when the 2002 Order came into force
// (7 August 2002), which is further back than any debt can be pursued.
//
// KEEPING IT CURRENT. A change the table hasn't heard of is charged at the
// rate before it, without a word, so the table has to be checked after every
// reference date. `test/base-rate-history.test.mjs` fails a week after a 30
// June or 31 December that BANK_RATE_CHECKED_ON is older than, which is CI on
// every pull request. To update it: open the source above, add any change
// since the last entry here as { from: "YYYY-MM-DD", ratePercent } (the day it
// took effect, newest last), set BANK_RATE_CHECKED_ON to the day you looked,
// and run `npm run build:ios-bundle` (the app carries this table too).

export const BANK_RATE_SOURCE = "https://www.bankofengland.co.uk/boeapps/database/Bank-Rate.asp";
export const BANK_RATE_CHECKED_ON = "2026-10-15";

// Each change: the day it took effect and the rate from then on. Oldest first.
export const BANK_RATE_CHANGES = Object.freeze([
  { from: "2001-11-08", ratePercent: 4.0 },
  { from: "2003-02-06", ratePercent: 3.75 },
  { from: "2003-07-10", ratePercent: 3.5 },
  { from: "2003-11-06", ratePercent: 3.75 },
  { from: "2004-02-05", ratePercent: 4.0 },
  { from: "2004-05-06", ratePercent: 4.25 },
  { from: "2004-06-10", ratePercent: 4.5 },
  { from: "2004-08-05", ratePercent: 4.75 },
  { from: "2005-08-04", ratePercent: 4.5 },
  { from: "2006-08-03", ratePercent: 4.75 },
  { from: "2006-11-09", ratePercent: 5.0 },
  { from: "2007-01-11", ratePercent: 5.25 },
  { from: "2007-05-10", ratePercent: 5.5 },
  { from: "2007-07-05", ratePercent: 5.75 },
  { from: "2007-12-06", ratePercent: 5.5 },
  { from: "2008-02-07", ratePercent: 5.25 },
  { from: "2008-04-10", ratePercent: 5.0 },
  { from: "2008-10-08", ratePercent: 4.5 },
  { from: "2008-11-06", ratePercent: 3.0 },
  { from: "2008-12-04", ratePercent: 2.0 },
  { from: "2009-01-08", ratePercent: 1.5 },
  { from: "2009-02-05", ratePercent: 1.0 },
  { from: "2009-03-05", ratePercent: 0.5 },
  { from: "2016-08-04", ratePercent: 0.25 },
  { from: "2017-11-02", ratePercent: 0.5 },
  { from: "2018-08-02", ratePercent: 0.75 },
  { from: "2020-03-11", ratePercent: 0.25 },
  { from: "2020-03-19", ratePercent: 0.1 },
  { from: "2021-12-16", ratePercent: 0.25 },
  { from: "2022-02-03", ratePercent: 0.5 },
  { from: "2022-03-17", ratePercent: 0.75 },
  { from: "2022-05-05", ratePercent: 1.0 },
  { from: "2022-06-16", ratePercent: 1.25 },
  { from: "2022-08-04", ratePercent: 1.75 },
  { from: "2022-09-22", ratePercent: 2.25 },
  { from: "2022-11-03", ratePercent: 3.0 },
  { from: "2022-12-15", ratePercent: 3.5 },
  { from: "2023-02-02", ratePercent: 4.0 },
  { from: "2023-03-23", ratePercent: 4.25 },
  { from: "2023-05-11", ratePercent: 4.5 },
  { from: "2023-06-22", ratePercent: 5.0 },
  { from: "2023-08-03", ratePercent: 5.25 },
  { from: "2024-08-01", ratePercent: 5.0 },
  { from: "2024-11-07", ratePercent: 4.75 },
  { from: "2025-02-06", ratePercent: 4.5 },
  { from: "2025-05-08", ratePercent: 4.25 },
  { from: "2025-08-07", ratePercent: 4.0 },
  { from: "2025-12-18", ratePercent: 3.75 }
].map((change) => Object.freeze(change)));

// The Act's eight points over the reference rate.
export const STATUTORY_UPLIFT_PERCENT = 8;

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

// "YYYY-MM-DD", or null for anything that isn't a day the calendar has.
function asDay(value) {
  const text = String(value ?? "").slice(0, 10);
  const match = DAY.exec(text);
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return text;
}

/**
 * Bank Rate in force on a day, in percent: the last change on or before it.
 * A change takes effect on the day it is announced, so a day with a change
 * reads the new rate. Before the table starts, its first rate; null for a
 * value that isn't a day.
 */
export function bankRateOn(day) {
  const text = asDay(day);
  if (!text) return null;
  let rate = BANK_RATE_CHANGES[0].ratePercent;
  for (const change of BANK_RATE_CHANGES) {
    if (change.from <= text) rate = change.ratePercent;
    else break;
  }
  return rate;
}

/**
 * The reference date for interest that starts to run on `interestFrom`: the
 * 30 June or 31 December immediately before it. Interest starting 1 January
 * to 30 June looks back to the 31 December before; 1 July to 31 December, to
 * the 30 June of the same year. Null for a value that isn't a day.
 */
export function referenceDateFor(interestFrom) {
  const text = asDay(interestFrom);
  if (!text) return null;
  const year = Number(text.slice(0, 4));
  const month = Number(text.slice(5, 7));
  return month <= 6 ? `${year - 1}-12-31` : `${year}-06-30`;
}

/**
 * The rate a debt carries, from the day its interest starts (the day after it
 * was due): { referenceDate, baseRatePercent, ratePercent }. The rate is a
 * number to two places, as Bank Rate is quoted, so 3.75 + 8 is 11.75 and
 * never 11.749999.
 */
export function statutoryRateFor(interestFrom) {
  const referenceDate = referenceDateFor(interestFrom);
  if (!referenceDate) return null;
  const baseRatePercent = bankRateOn(referenceDate);
  const ratePercent = Math.round((baseRatePercent + STATUTORY_UPLIFT_PERCENT) * 100) / 100;
  return { referenceDate, baseRatePercent, ratePercent };
}

/**
 * The newest reference date on or before `today`, a day: what the table must
 * have been checked since for every debt starting now to carry the right rate.
 */
export function latestReferenceDate(today) {
  const text = asDay(today);
  if (!text) return null;
  const year = Number(text.slice(0, 4));
  const midYear = `${year}-06-30`;
  if (text >= `${year}-12-31`) return `${year}-12-31`;
  if (text >= midYear) return midYear;
  return `${year - 1}-12-31`;
}
